import { BaseEditor, Editor } from 'vendor/slate'
import { History } from './history'

/**
 * Weakmaps for attaching state to the editor.
 */

export const HISTORY = new WeakMap<Editor, History>()
export const SAVING = new WeakMap<Editor, boolean | undefined>()
export const MERGING = new WeakMap<Editor, boolean | undefined>()
export const SPLITTING_ONCE = new WeakMap<Editor, boolean | undefined>()

/**
 * `HistoryEditor` contains helpers for history-enabled editors.
 */

export interface HistoryEditor extends BaseEditor {
  history: History
  undo: () => void
  redo: () => void
  resetHistory(): void
}

export const HistoryEditor = {
  /**
   * Check if a value is a `HistoryEditor` object.
   */

  isHistoryEditor(value: any): value is HistoryEditor {
    return History.isHistory(value.history) && Editor.isEditor(value)
  },

  /**
   * Get the merge flag's current value.
   */

  isMerging(editor: HistoryEditor): boolean | undefined {
    return MERGING.get(editor)
  },

  /**
   * Get the split-once flag's current value.
   */

  isSplittingOnce(editor: HistoryEditor): boolean | undefined {
    return SPLITTING_ONCE.get(editor)
  },

  /**
   * Get the saving flag's current value.
   */

  isSaving(editor: HistoryEditor): boolean | undefined {
    return SAVING.get(editor)
  },

  /**
   * Redo to the previous saved state.
   */

  redo(editor: HistoryEditor): void {
    editor.redo()
  },

  /**
   * Undo to the previous saved state.
   */

  undo(editor: HistoryEditor): void {
    editor.undo()
  },

  /**
   * Apply a series of changes inside a synchronous `fn`, without merging any of
   * the new operations into previous save point in the history.
   */

  withoutMerging(editor: HistoryEditor, fn: () => void): void {
    const prev = HistoryEditor.isMerging(editor)
    MERGING.set(editor, false)
    try {
      fn()
    } finally {
      MERGING.set(editor, prev)
    }
  },

  /**
   * Apply a series of changes inside a synchronous `fn`, merging all operations
   * into the previous history batch when possible.
   */

  withMerging(editor: HistoryEditor, fn: () => void): void {
    const prev = HistoryEditor.isMerging(editor)
    MERGING.set(editor, true)
    try {
      fn()
    } finally {
      MERGING.set(editor, prev)
    }
  },

  /**
   * Apply a series of changes inside a synchronous `fn`, starting a fresh
   * history batch for the first operation and merging subsequent operations.
   */

  withNewBatch(editor: HistoryEditor, fn: () => void): void {
    const prevMerging = HistoryEditor.isMerging(editor)
    const prevSplitting = HistoryEditor.isSplittingOnce(editor)
    MERGING.set(editor, true)
    SPLITTING_ONCE.set(editor, true)
    try {
      fn()
    } finally {
      MERGING.set(editor, prevMerging)
      SPLITTING_ONCE.set(editor, prevSplitting)
    }
  },

  /**
   * Apply a series of changes inside a synchronous `fn`, without saving any of
   * their operations into the history.
   */

  withoutSaving(editor: HistoryEditor, fn: () => void): void {
    const prev = HistoryEditor.isSaving(editor)
    SAVING.set(editor, false)
    try {
      fn()
    } finally {
      SAVING.set(editor, prev)
    }
  },
}
